import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Employee } from '@app/features/employees/employee.model';
import { EmployeeService } from '@app/features/employees/employee.service';
import { SsnFormatterPipe } from '@app/shared/forms/ssn-formatter.pipe';
import { PhoneNumberPipe } from '@app/shared/pipes/phone-number.pipe';
import { LucideAtSign, LucideIdCard, LucidePhone, LucideTag } from '@lucide/angular';
@Component({
  selector: 'app-search-employee',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatAutocompleteModule,
    MatIconModule,
    MatTooltipModule,
    SsnFormatterPipe,
    PhoneNumberPipe,
    LucideTag,
    LucideAtSign,
    LucideIdCard,
    LucidePhone,
  ],
  templateUrl: './search-employee.html',
  styleUrls: ['./search-employee.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchEmployee),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SearchEmployee),
      multi: true,
    },
  ],
})
export class SearchEmployee implements OnInit, ControlValueAccessor, Validator {
  label = input('Employé');
  showHint = input(false);

  @Input() required: boolean | null | undefined;
  @Input() value: Employee | undefined;
  @ViewChild('employeeInput', { static: false })
  employeeInput?: ElementRef<HTMLInputElement>;
  private injector = inject(Injector);
  private employeeService = inject(EmployeeService);
  private cdr = inject(ChangeDetectorRef);
  private userInput = '';
  searchResults: Employee[] = [];
  ngControl?: NgControl;
  disabled = false;

  public autoComplete(event: KeyboardEvent) {
    const htmlInputValue = (event.target as HTMLInputElement).value;
    if (htmlInputValue.length < 3) {
      this.searchResults = [];
    } else if (this.userInput.toLowerCase() != htmlInputValue.toLowerCase()) {
      this.employeeService.search(htmlInputValue).subscribe({
        next: (res) => {
          this.searchResults = res;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error fetching search results:', err);
          this.searchResults = [];
          this.cdr.markForCheck();
        },
      });
    }
    this.userInput = (event.target as HTMLInputElement).value;
  }

  formatEmployee(employee: Employee): string {
    return `${employee.firstname} ${employee.lastname}`;
  }

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null) ?? undefined;
    if (this.ngControl) {
      try {
        this.ngControl.valueAccessor = this;
      } catch {
        console.warn(
          'Impossible de définir le valueAccessor. Assurez-vous que ce composant est utilisé dans un contexte de formulaire (FormGroup, FormControl, etc.)',
        );
      }
    }
  }

  onChange: (value: Employee | undefined) => void = () => {
    // No-op
  };
  onTouched: () => void = () => {
    // No-op
  };

  writeValue(value: Employee | undefined): void {
    this.value = value;
  }

  registerOnChange(fn: (value: Employee | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const ctrl = control ?? this.control;
    const isRequired = this.required || (ctrl ? this.hasRequiredValidator(ctrl) : false);

    if (isRequired && !this.value) {
      return { required: true };
    }

    return null;
  }

  private hasRequiredValidator(control: AbstractControl): boolean {
    if (!control.validator) return false;

    const validator = control.validator({} as AbstractControl);
    return !!(validator && validator['required']);
  }

  /** Retourne le control du FormGroup / FormControl auquel ce composant est lié, si présent */
  get control(): AbstractControl | null {
    return this.ngControl ? (this.ngControl.control ?? null) : null;
  }

  onSelectionChange(value: Employee): void {
    this.value = value;
    this.userInput = this.formatEmployee(value);
    this.onChange(this.value);
    this.onTouched();
  }

  clear(): void {
    this.value = undefined;
    this.userInput = '';
    this.searchResults = [];
    if (this.employeeInput) {
      this.employeeInput.nativeElement.value = '';
    }
    this.onChange(this.value);
    this.onTouched();
    this.cdr.markForCheck();
  }
}
